// js/AdminBoard/CustomerSupportBoard/CustomerSupport.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {
    customerSupportSearch,
    fetchDeviceTypes,
} from "js/Api/customerSupportApi";
import DeviceDetail from "js/AdminBoard/CustomerSupportBoard/DeviceDetail/DeviceDetail";
import CallLogsBulkExport from "js/AdminBoard/CustomerSupportBoard/CallLogsBulkExport";

function CustomerSupport({ userInfo }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [activeView, setActiveView] = useState("device-search");

    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 100,
        totalCount: 0,
        totalPages: 0,
    });

    const [selectedDeviceId, setSelectedDeviceId] = useState(
        searchParams.get("deviceId")
    );
    const [selectedDevice, setSelectedDevice] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({
        deviceType: "",
        isLeader: "",
        createdAfter: "",
        createdBefore: "",
        sortBy: "CreatedAt",
        sortOrder: "desc",
    });

    const [deviceTypes, setDeviceTypes] = useState([]);
    const [highlightedSearchTerm, setHighlightedSearchTerm] = useState("");
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    const parseIsLeaderFilter = (value) => {
        if (value === "true") return true;
        if (value === "false") return false;
        return undefined;
    };

    const fetchDevices = async (page = 1) => {
        setLoading(true);
        try {
            const result = await customerSupportSearch({
                page,
                pageSize: pagination.pageSize,
                includeProfilePicture: false,
                searchTerm: searchTerm.trim(),
                deviceType: filters.deviceType,
                isLeader: parseIsLeaderFilter(filters.isLeader),
                createdAfter: filters.createdAfter,
                createdBefore: filters.createdBefore,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            });

            if (!result.success) {
                if (result.status === 401) {
                    navigate("/");
                }
                throw new Error(result.error || "Error fetching devices");
            }

            const {
                devices: rawDevices,
                pagination: pg,
                searchTerm: returnedSearchTerm,
            } = result.data;

            setDevices(rawDevices || []);
            setPagination(pg);
            setHighlightedSearchTerm(returnedSearchTerm || "");
        } catch (error) {
            console.error("Error fetching devices:", error);
            toast.error("Error fetching devices. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const loadDeviceTypes = async () => {
        const result = await fetchDeviceTypes();
        if (!result.success) {
            console.error("Error fetching device types:", result.error);
            return;
        }
        setDeviceTypes(result.data || []);
    };

    useEffect(() => {
        loadDeviceTypes();
    }, []);

    useEffect(() => {
        const idFromUrl = searchParams.get("deviceId");
        setSelectedDeviceId(idFromUrl);
    }, [searchParams]);

    useEffect(() => {
        if (!selectedDeviceId) {
            setSelectedDevice(null);
            return;
        }
        const dev = devices.find((d) => d.id === selectedDeviceId);
        setSelectedDevice(dev || null);
    }, [selectedDeviceId, devices]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchDevices(1);
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setSearchTerm("");
        setFilters({
            deviceType: "",
            isLeader: "",
            createdAfter: "",
            createdBefore: "",
            sortBy: "CreatedAt",
            sortOrder: "desc",
        });
        fetchDevices(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchDevices(newPage);
        }
    };

    const viewDeviceDetails = (deviceId) => {
        navigate({
            pathname: location.pathname,
            search: `?deviceId=${deviceId}`,
        });
    };

    const escapeRegExp = (string) =>
        string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const highlightText = (text, term) => {
        if (!text || !term) return text;
        const safeTerm = escapeRegExp(term);
        const parts = text.toString().split(new RegExp(`(${safeTerm})`, "gi"));

        return parts.map((part, index) =>
            part.toLowerCase() === term.toLowerCase() ? (
                <mark
                    key={index}
                    style={{ backgroundColor: "#ffeb3b", padding: "2px" }}
                >
                    {part}
                </mark>
            ) : (
                part
            )
        );
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleString();
    };

    const truncateText = (text, maxLength = 30) => {
        if (!text) return "N/A";
        const str = text.toString();
        return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
    };

    return (
        <div className="customer-support-container">
            <div className="row">
                <div className="col-md-12">
                    <div className="white_shd full margin_bottom_30">
                        <div className="full graph_head">
                            <div
                                className="heading1 margin_0"
                                style={{ display: "flex", alignItems: "center" }}
                            >
                                {selectedDeviceId && (
                                    <ArrowBackRoundedIcon
                                        onClick={() => navigate(-1)}
                                        sx={{
                                            fontSize: "2rem",
                                            color: "black",
                                            marginRight: "10px",
                                            cursor: "pointer",
                                        }}
                                    />
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",
                                        flexWrap: "wrap",
                                        gap: "12px",
                                    }}
                                >
                                    <h2 style={{ marginBottom: 0 }}>
                                        <i className="fa fa-headphones"></i>{" "}
                                        {activeView === "call-logs-bulk-export"
                                            ? "Customer Support - Call Logs Bulk Export"
                                            : selectedDeviceId
                                            ? "Customer Support - Device Detail"
                                            : "Customer Support - Device Search"}
                                    </h2>

                                    {!selectedDeviceId && (
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <button
                                                className={`btn ${
                                                    activeView === "device-search"
                                                        ? "btn-primary"
                                                        : "btn-outline-primary"
                                                }`}
                                                onClick={() => setActiveView("device-search")}
                                            >
                                                Device Search
                                            </button>
                                            <button
                                                className={`btn ${
                                                    activeView === "call-logs-bulk-export"
                                                        ? "btn-primary"
                                                        : "btn-outline-primary"
                                                }`}
                                                onClick={() =>
                                                    setActiveView("call-logs-bulk-export")
                                                }
                                            >
                                                Call Logs Bulk Export
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="full price_table padding_infor_info">
                            {selectedDeviceId ? (
                                <DeviceDetail
                                    deviceId={selectedDeviceId}
                                    device={selectedDevice}
                                    onBack={() => navigate(-1)}
                                />
                            ) : activeView === "call-logs-bulk-export" ? (
                                <CallLogsBulkExport
                                    userInfo={userInfo}
                                    onBack={() => setActiveView("device-search")}
                                />
                            ) : (
                                <>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div
                                                className="search-section"
                                                style={{
                                                    marginBottom: "30px",
                                                    padding: "20px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <form onSubmit={handleSearch}>
                                                    <div className="row">
                                                        <div className="col-md-10">
                                                            <div className="form-group">
                                                                <label
                                                                    style={{
                                                                        fontWeight: "bold",
                                                                        marginBottom: "10px",
                                                                    }}
                                                                >
                                                                    <i className="fa fa-search"></i> Universal
                                                                    Search
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Search by Device ID, Account ID, Name (first/last), Serial Number, Email, Recovery Email, Konnekt ID, Org Code..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                    style={{ fontSize: "16px", padding: "12px" }}
                                                                />
                                                                <small className="form-text text-muted">
                                                                    💡 Tip: You can search with partial
                                                                    information. For example: "Terry", "xxxx1234",
                                                                    "john@", etc.
                                                                </small>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <div
                                                                className="form-group"
                                                                style={{ marginTop: "30px" }}
                                                            >
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary btn-block"
                                                                    style={{ padding: "12px" }}
                                                                >
                                                                    <i className="fa fa-search"></i> Search
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-12">
                                            <div style={{ marginBottom: "20px" }}>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() =>
                                                        setShowAdvancedFilters(!showAdvancedFilters)
                                                    }
                                                    style={{ marginBottom: "10px" }}
                                                >
                                                    <i
                                                        className={`fa fa-${
                                                            showAdvancedFilters ? "minus" : "plus"
                                                        }`}
                                                    ></i>{" "}
                                                    Advanced Filters
                                                </button>

                                                {showAdvancedFilters && (
                                                    <div
                                                        style={{
                                                            padding: "20px",
                                                            border: "1px solid #dee2e6",
                                                            borderRadius: "4px",
                                                            backgroundColor: "#fff",
                                                        }}
                                                    >
                                                        <div className="row">
                                                            <div className="col-md-3">
                                                                <div className="form-group">
                                                                    <label>Device Type</label>
                                                                    <select
                                                                        className="form-control"
                                                                        value={filters.deviceType}
                                                                        onChange={(e) =>
                                                                            handleFilterChange(
                                                                                "deviceType",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">All Types</option>
                                                                        {deviceTypes.map((type) => (
                                                                            <option key={type} value={type}>
                                                                                {type}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <div className="form-group">
                                                                    <label>Manager Status</label>
                                                                    <select
                                                                        className="form-control"
                                                                        value={filters.isLeader}
                                                                        onChange={(e) =>
                                                                            handleFilterChange(
                                                                                "isLeader",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="">All</option>
                                                                        <option value="true">Manager</option>
                                                                        <option value="false">Not Manager</option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <div className="form-group">
                                                                    <label>Sort By</label>
                                                                    <select
                                                                        className="form-control"
                                                                        value={filters.sortBy}
                                                                        onChange={(e) =>
                                                                            handleFilterChange(
                                                                                "sortBy",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="CreatedAt">
                                                                            Created Date
                                                                        </option>
                                                                        <option value="UpdatedAt">
                                                                            Updated Date
                                                                        </option>
                                                                        <option value="Name">Name</option>
                                                                        <option value="AccountName">
                                                                            Account Name
                                                                        </option>
                                                                    </select>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <div className="form-group">
                                                                    <label>Order</label>
                                                                    <select
                                                                        className="form-control"
                                                                        value={filters.sortOrder}
                                                                        onChange={(e) =>
                                                                            handleFilterChange(
                                                                                "sortOrder",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    >
                                                                        <option value="desc">Newest First</option>
                                                                        <option value="asc">Oldest First</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row">
                                                            <div className="col-md-4">
                                                                <div className="form-group">
                                                                    <label>Created After</label>
                                                                    <input
                                                                        type="date"
                                                                        className="form-control"
                                                                        value={filters.createdAfter}
                                                                        onChange={(e) =>
                                                                            handleFilterChange(
                                                                                "createdAfter",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-md-4">
                                                                <div className="form-group">
                                                                    <label>Created Before</label>
                                                                    <input
                                                                        type="date"
                                                                        className="form-control"
                                                                        value={filters.createdBefore}
                                                                        onChange={(e) =>
                                                                            handleFilterChange(
                                                                                "createdBefore",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="col-md-4">
                                                                <div
                                                                    className="form-group"
                                                                    style={{ marginTop: "25px" }}
                                                                >
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        onClick={() => fetchDevices(1)}
                                                                        style={{ marginRight: "10px" }}
                                                                    >
                                                                        <i className="fa fa-check"></i> Apply
                                                                        Filters
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-secondary"
                                                                        onClick={resetFilters}
                                                                    >
                                                                        <i className="fa fa-refresh"></i> Reset All
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-12">
                                            {loading ? (
                                                <div
                                                    className="text-center"
                                                    style={{ padding: "50px" }}
                                                >
                                                    <i
                                                        className="fa fa-spinner fa-spin"
                                                        style={{ fontSize: "48px", color: "#007bff" }}
                                                    ></i>
                                                    <p
                                                        style={{ marginTop: "20px", fontSize: "18px" }}
                                                    >
                                                        Searching devices...
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    {devices.length > 0 && (
                                                        <div
                                                            style={{
                                                                marginBottom: "15px",
                                                                padding: "10px",
                                                                backgroundColor: "#d1ecf1",
                                                                borderRadius: "4px",
                                                            }}
                                                        >
                                                            <strong>
                                                                Found {pagination.totalCount} device(s)
                                                            </strong>
                                                            {highlightedSearchTerm && (
                                                                <span>
                                                                    {" "}
                                                                    matching "{highlightedSearchTerm}"
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div className="table-responsive">
                                                        <table className="table table-hover table-bordered">
                                                            <thead className="thead-light">
                                                                <tr>
                                                                    <th>Device ID</th>
                                                                    <th>Account ID</th>
                                                                    <th>Name</th>
                                                                    <th>Serial No</th>
                                                                    <th>Email</th>
                                                                    <th>Device Type</th>
                                                                    <th>Linked Devices</th>
                                                                    <th>Created</th>
                                                                    <th>Updated</th>
                                                                    <th>Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {devices.length === 0 ? (
                                                                    <tr>
                                                                        <td
                                                                            colSpan="10"
                                                                            className="text-center"
                                                                            style={{ padding: "50px" }}
                                                                        >
                                                                            <i
                                                                                className="fa fa-search"
                                                                                style={{
                                                                                    fontSize: "48px",
                                                                                    color: "#ccc",
                                                                                }}
                                                                            ></i>
                                                                            <p
                                                                                style={{
                                                                                    marginTop: "20px",
                                                                                    fontSize: "16px",
                                                                                    color: "#666",
                                                                                }}
                                                                            >
                                                                                No devices found. Try different search
                                                                                terms.
                                                                            </p>
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    devices.map((device) => (
                                                                        <tr
                                                                            key={device.id}
                                                                            onClick={() =>
                                                                                viewDeviceDetails(device.id)
                                                                            }
                                                                            style={{ cursor: "pointer" }}
                                                                        >
                                                                            <td
                                                                                style={{
                                                                                    fontFamily: "monospace",
                                                                                    fontSize: "12px",
                                                                                }}
                                                                            >
                                                                                {highlightText(
                                                                                    truncateText(device.id, 20),
                                                                                    highlightedSearchTerm
                                                                                )}
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            device.id || "N/A"
                                                                                        );
                                                                                    }}
                                                                                    title="Copy ID"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td
                                                                                style={{
                                                                                    fontFamily: "monospace",
                                                                                    fontSize: "12px",
                                                                                }}
                                                                            >
                                                                                {highlightText(
                                                                                    truncateText(device.accountId, 20),
                                                                                    highlightedSearchTerm
                                                                                )}
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            device.accountId || "N/A"
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Account ID"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td>
                                                                                <strong>
                                                                                    {highlightText(
                                                                                        device.name || "N/A",
                                                                                        highlightedSearchTerm
                                                                                    )}
                                                                                </strong>
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            device.name || "N/A"
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Name"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                                <br />
                                                                                {device.isLeader && (
                                                                                    <span className="badge badge-warning">
                                                                                        Manager
                                                                                    </span>
                                                                                )}
                                                                            </td>

                                                                            <td style={{ fontFamily: "monospace" }}>
                                                                                {highlightText(
                                                                                    device.serialNo || "N/A",
                                                                                    highlightedSearchTerm
                                                                                )}
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            device.serialNo || "N/A"
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Serial No"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td style={{ fontSize: "12px" }}>
                                                                                {highlightText(
                                                                                    truncateText(device.userEmail, 25),
                                                                                    highlightedSearchTerm
                                                                                )}
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            device.userEmail || "N/A"
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Email"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td>
                                                                                <span className="badge badge-info">
                                                                                    {highlightText(
                                                                                        device.deviceType || "N/A",
                                                                                        highlightedSearchTerm
                                                                                    )}
                                                                                </span>
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            device.deviceType || "N/A"
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Device Type"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td className="text-center">
                                                                                <span className="badge badge-secondary">
                                                                                    {device.linkedDevicesCount - 1 || 0}
                                                                                </span>
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            String(
                                                                                                device.linkedDevicesCount - 1 || 0
                                                                                            )
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Linked Devices Count"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td style={{ fontSize: "12px" }}>
                                                                                {formatDate(device.createdAt)}
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            formatDate(device.createdAt)
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Created Time"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td style={{ fontSize: "12px" }}>
                                                                                {formatDate(device.updatedAt)}
                                                                                <button
                                                                                    className="btn btn-sm btn-link"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        copyToClipboard(
                                                                                            formatDate(device.updatedAt)
                                                                                        );
                                                                                    }}
                                                                                    title="Copy Updated Time"
                                                                                >
                                                                                    <i className="fa fa-copy"></i>
                                                                                </button>
                                                                            </td>

                                                                            <td>
                                                                                <button
                                                                                    className="btn btn-sm btn-primary"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        viewDeviceDetails(device.id);
                                                                                    }}
                                                                                >
                                                                                    <i className="fa fa-eye"></i> View
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {devices.length > 0 && (
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <p>
                                                                    Showing{" "}
                                                                    {(pagination.currentPage - 1) *
                                                                        pagination.pageSize +
                                                                        1}{" "}
                                                                    to{" "}
                                                                    {Math.min(
                                                                        pagination.currentPage *
                                                                            pagination.pageSize,
                                                                        pagination.totalCount
                                                                    )}{" "}
                                                                    of {pagination.totalCount} devices
                                                                </p>
                                                            </div>
                                                            <div className="col-md-6">
                                                                <nav>
                                                                    <ul className="pagination justify-content-end">
                                                                        <li
                                                                            className={`page-item ${
                                                                                pagination.currentPage === 1
                                                                                    ? "disabled"
                                                                                    : ""
                                                                            }`}
                                                                        >
                                                                            <button
                                                                                className="page-link"
                                                                                onClick={() =>
                                                                                    handlePageChange(
                                                                                        pagination.currentPage - 1
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    pagination.currentPage === 1
                                                                                }
                                                                            >
                                                                                Previous
                                                                            </button>
                                                                        </li>

                                                                        {[...Array(pagination.totalPages)].map(
                                                                            (_, index) => {
                                                                                const page = index + 1;
                                                                                if (
                                                                                    page === 1 ||
                                                                                    page === pagination.totalPages ||
                                                                                    (page >=
                                                                                        pagination.currentPage - 2 &&
                                                                                        page <=
                                                                                            pagination.currentPage + 2)
                                                                                ) {
                                                                                    return (
                                                                                        <li
                                                                                            key={page}
                                                                                            className={`page-item ${
                                                                                                pagination.currentPage ===
                                                                                                page
                                                                                                    ? "active"
                                                                                                    : ""
                                                                                            }`}
                                                                                        >
                                                                                            <button
                                                                                                className="page-link"
                                                                                                onClick={() =>
                                                                                                    handlePageChange(page)
                                                                                                }
                                                                                            >
                                                                                                {page}
                                                                                            </button>
                                                                                        </li>
                                                                                    );
                                                                                } else if (
                                                                                    page ===
                                                                                        pagination.currentPage - 3 ||
                                                                                    page ===
                                                                                        pagination.currentPage + 3
                                                                                ) {
                                                                                    return (
                                                                                        <li
                                                                                            key={page}
                                                                                            className="page-item disabled"
                                                                                        >
                                                                                            <span className="page-link">
                                                                                                ...
                                                                                            </span>
                                                                                        </li>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            }
                                                                        )}

                                                                        <li
                                                                            className={`page-item ${
                                                                                pagination.currentPage ===
                                                                                pagination.totalPages
                                                                                    ? "disabled"
                                                                                    : ""
                                                                            }`}
                                                                        >
                                                                            <button
                                                                                className="page-link"
                                                                                onClick={() =>
                                                                                    handlePageChange(
                                                                                        pagination.currentPage + 1
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    pagination.currentPage ===
                                                                                    pagination.totalPages
                                                                                }
                                                                            >
                                                                                Next
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CustomerSupport;